import { useState } from "react";
import { Check, Lock, Plus, X } from "lucide-react";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, Card, cx, Field, NumberStepper, Segmented, TextField } from "@/components/ui";
import { IconPicker, Points } from "@/components/bits";
import { api } from "@/lib/api";
import { REPEAT_LABELS } from "@/lib/format";
import { useAction, useKidJobs } from "@/lib/queries";
import { useSession } from "@/lib/session";
import type { GateDTO, JobDTO, JobRunDTO, KidJobDTO, MemberDTO } from "@shared/types";

/** Phones: the job on one line and its buttons on the next, so titles aren't squeezed. Wider: one line. */
const ROW = "flex flex-col gap-2 rounded-2xl px-3 py-2 sm:flex-row sm:items-center sm:gap-3";

function lockNote(kid: MemberDTO, gate: GateDTO) {
  return `Jobs unlock when ${kid.name}'s responsibilities are done (${gate.done}/${gate.total})`;
}

/**
 * The jobs a kid can earn from, on their page: what's on their to-do list (with "Done, pay it"),
 * what they could do next, and a way to assign one. Handy for younger kids who won't browse or
 * claim jobs themselves. Finished jobs waiting for a check are in "Needs you" above.
 */
export function KidJobs({ kid }: { kid: MemberDTO }) {
  const { family } = useSession();
  const { data } = useKidJobs(kid.id);
  const [assigning, setAssigning] = useState(false);
  const pay = useAction(
    (v: { job: KidJobDTO; run?: JobRunDTO | null }) =>
      v.run
        ? api.post<JobRunDTO>(`/job-runs/${v.run.id}/review`, { action: "approve" })
        : api.post<JobRunDTO>(`/jobs/${v.job.id}/claim`, { kidId: kid.id, pay: true }),
    { success: (r) => `Paid ${r.rewardPaid} ${family.currencyName} to ${kid.name} 🎉` },
  );
  const assign = useAction((job: KidJobDTO) => api.post(`/jobs/${job.id}/claim`, { kidId: kid.id }), {
    success: `Added to ${kid.name}'s jobs`,
  });
  const remove = useAction((run: JobRunDTO) => api.post(`/job-runs/${run.id}/cancel`), { success: "Taken off the list" });

  if (!data) return null;
  const { gate } = data;
  const jobs = data.jobs.filter((j) => !j.workItOff || j.available || j.activeRun);
  const todo = jobs.filter((j) => j.activeRun?.status === "claimed");
  const open = jobs.filter((j) => !j.activeRun && j.available);
  const later = jobs.filter((j) => !j.activeRun && !j.available);
  const busy = (job: KidJobDTO) =>
    (pay.isPending && pay.variables?.job.id === job.id) || (assign.isPending && assign.variables?.id === job.id);

  return (
    <>
      <div className="mb-2 mt-6 flex items-center justify-between gap-2 px-1">
        <h2 className="text-lg font-extrabold">Jobs to earn</h2>
        <Button size="sm" variant="soft" className="h-11" onClick={() => setAssigning(true)}>
          <Plus className="size-4" /> Assign a job
        </Button>
      </div>
      <Card className="space-y-2">
        {!gate.unlocked && (
          <p className="flex items-center gap-2 rounded-2xl bg-sunken px-3 py-2 text-sm font-bold text-muted">
            <Lock className="size-4 shrink-0" /> {lockNote(kid, gate)}
          </p>
        )}
        {jobs.length === 0 && <p className="text-sm text-muted">No jobs for {kid.name} yet. Assign one to get them earning.</p>}

        {todo.map((j) => (
          <div key={j.id} className={ROW + " bg-coin-soft/60"} data-testid="kid-job">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="text-2xl">{j.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold">{j.title}</div>
                <div className="flex items-center gap-1.5 text-xs font-bold text-muted">
                  <Badge tone="warn">To do</Badge> <Points value={j.reward} family={family} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-2 sm:flex">
              <Button
                variant="secondary"
                size="sm"
                className="size-11 shrink-0 px-0"
                aria-label={`Take ${j.title} off ${kid.name}'s list`}
                onClick={() => j.activeRun && remove.mutate(j.activeRun)}
              >
                <X className="size-4" />
              </Button>
              <Button size="sm" className="h-11" onClick={() => pay.mutate({ job: j, run: j.activeRun })} loading={busy(j)}>
                <Check className="size-4" /> Done, pay it
              </Button>
            </div>
          </div>
        ))}

        {open.map((j) => (
          <div key={j.id} className={ROW + " bg-sunken"} data-testid="kid-job">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="text-2xl">{j.icon}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate font-bold">{j.title}</div>
                <div className="text-xs font-bold text-muted">
                  <Points value={j.reward} family={family} /> · {REPEAT_LABELS[j.repeat]}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Button
                variant="secondary"
                size="sm"
                className="h-11"
                disabled={!gate.unlocked}
                loading={assign.isPending && assign.variables?.id === j.id}
                onClick={() => assign.mutate(j)}
                aria-label={`Assign ${j.title} to ${kid.name}`}
              >
                Assign
              </Button>
              <Button
                size="sm"
                className="h-11"
                disabled={!gate.unlocked}
                loading={pay.isPending && pay.variables?.job.id === j.id}
                onClick={() => pay.mutate({ job: j })}
                aria-label={`${j.title} is done, pay ${kid.name}`}
              >
                <Check className="size-4" /> Done, pay it
              </Button>
            </div>
          </div>
        ))}

        {later.map((j) => (
          <div key={j.id} className="flex items-center gap-3 px-3 py-1.5 text-muted">
            <span className="text-xl opacity-60">{j.icon}</span>
            <div className="min-w-0 flex-1 truncate text-sm font-bold">{j.title}</div>
            <span className="shrink-0 text-xs font-bold">{j.unavailableReason}</span>
          </div>
        ))}
      </Card>
      {assigning && <AssignJobSheet kid={kid} gate={gate} jobs={open} onClose={() => setAssigning(false)} />}
    </>
  );
}

const REPEATS = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "anytime", label: "Any time" },
] as const;

/** Pick one of the jobs this kid can do, or quick-create one just for them; assign it or pay it now. */
function AssignJobSheet({ kid, gate, jobs, onClose }: { kid: MemberDTO; gate: GateDTO; jobs: KidJobDTO[]; onClose: () => void }) {
  const { family } = useSession();
  const [mode, setMode] = useState<"pick" | "new">(jobs.length ? "pick" : "new");
  const [jobId, setJobId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState("🧰");
  const [reward, setReward] = useState(10);
  const [repeat, setRepeat] = useState<JobDTO["repeat"]>("once");
  const picked = jobs.find((j) => j.id === jobId) ?? null;

  const save = useAction(
    async (then: "assign" | "pay" | "add") => {
      if (mode === "pick") {
        if (!picked) return null;
        return api.post<JobRunDTO>(`/jobs/${picked.id}/claim`, { kidId: kid.id, pay: then === "pay" });
      }
      const base = { title: title.trim(), icon, reward, repeat, kidIds: [kid.id] };
      // Locked kids can still get the job for later; it just isn't on their to-do list yet.
      if (then === "add") return api.post<JobDTO>("/jobs", base);
      const created = await api.post<JobDTO & { run: JobRunDTO | null }>("/jobs", { ...base, assignTo: kid.id });
      if (then === "pay" && created.run) return api.post<JobRunDTO>(`/job-runs/${created.run.id}/review`, { action: "approve" });
      return created.run;
    },
    {
      success: (r) =>
        r && "rewardPaid" in r && r.status === "approved"
          ? `Paid ${r.rewardPaid} ${family.currencyName} to ${kid.name} 🎉`
          : `Added to ${kid.name}'s jobs`,
      onSuccess: onClose,
    },
  );
  const ready = mode === "pick" ? !!picked : title.trim().length > 0;

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Assign a job to ${kid.name}`}
      footer={
        gate.unlocked ? (
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="lg"
              variant="secondary"
              disabled={!ready}
              loading={save.isPending && save.variables === "assign"}
              onClick={() => save.mutate("assign")}
            >
              Assign
            </Button>
            <Button
              size="lg"
              disabled={!ready}
              loading={save.isPending && save.variables === "pay"}
              onClick={() => save.mutate("pay")}
            >
              <Check className="size-5" /> Done, pay it
            </Button>
          </div>
        ) : mode === "new" ? (
          <Button block size="lg" disabled={!ready} loading={save.isPending} onClick={() => save.mutate("add")}>
            Add to {kid.name}'s jobs
          </Button>
        ) : undefined
      }
    >
      <Segmented
        className="mb-4"
        value={mode}
        onChange={setMode}
        options={[
          { value: "pick", label: "Pick a job" },
          { value: "new", label: "New job" },
        ]}
      />
      {!gate.unlocked && (
        <p className="mb-4 flex items-center gap-2 rounded-2xl bg-sunken px-3 py-2 text-sm font-bold text-muted">
          <Lock className="size-4 shrink-0" /> {lockNote(kid, gate)}
        </p>
      )}
      {mode === "pick" ? (
        jobs.length === 0 ? (
          <p className="text-sm text-muted">No open jobs for {kid.name} right now. Make a new one.</p>
        ) : (
          <div className="space-y-2" role="radiogroup" aria-label="Job">
            {jobs.map((j) => (
              <button
                key={j.id}
                type="button"
                role="radio"
                aria-checked={j.id === jobId}
                onClick={() => setJobId(j.id)}
                className={cx(
                  "flex min-h-14 w-full items-center gap-3 rounded-2xl px-3 py-2 text-left ring-1 transition",
                  j.id === jobId ? "bg-brand-soft ring-2 ring-brand" : "bg-sunken ring-line",
                )}
              >
                <span className="text-2xl">{j.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold">{j.title}</span>
                  <span className="block text-xs font-bold text-muted">{REPEAT_LABELS[j.repeat]}</span>
                </span>
                <Points value={j.reward} family={family} className="font-extrabold text-coin-ink" />
              </button>
            ))}
          </div>
        )
      ) : (
        <div className="space-y-5">
          <TextField
            label="Job"
            value={title}
            maxLength={80}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Feed the fish"
          />
          <Field label="Icon">
            <IconPicker value={icon} onChange={setIcon} />
          </Field>
          <Field label={`Reward (${family.currencyName})`}>
            <NumberStepper
              value={reward}
              onChange={(v) => setReward(Math.round(v))}
              min={0}
              max={100_000}
              step={5}
              suffix={family.currencyIcon}
            />
          </Field>
          <Field label="How often">
            <Segmented value={repeat} onChange={setRepeat} options={[...REPEATS]} />
          </Field>
          <p className="text-xs text-muted">Only {kid.name} sees this job.</p>
        </div>
      )}
    </Sheet>
  );
}
